import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { motion } from "framer-motion";
import { ArrowRight, MagnifyingGlass, Users } from "@phosphor-icons/react";

export default function Authors() {
  const [authors, setAuthors] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const fetchAuthors = async (p = 1, query = "", append = false) => {
    setLoading(true);
    try {
      const { data } = await api.get("/authors", { params: { page: p, limit: 12, q: query } });
      if (append) {
        setAuthors(prev => [...(prev || []), ...data.authors]);
      } else {
        setAuthors(data.authors);
      }
      setMeta(data.meta);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      fetchAuthors(1, search, false);
    }, search ? 400 : 0);
    return () => clearTimeout(timer);
  }, [search]);

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchAuthors(next, search, true);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-20">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
        <div>
          <div className="eyebrow text-muted-foreground mb-3 flex items-center gap-2">
            <Users size={16} weight="duotone" /> The Collective
          </div>
          <h1 className="font-serif text-5xl sm:text-6xl font-black tracking-tight">Our Authors</h1>
          <p className="mt-4 text-muted-foreground max-w-lg">
            Discover the voices shaping Inkspot. From poetic explorers to technical architects — meet the people behind the prose.
          </p>
        </div>
        <div className="relative w-full max-w-sm">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search authors or sites..."
            className="w-full bg-card border border-border pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-foreground transition-all"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {authors === null ? (
        <div className="text-muted-foreground">Finding writers...</div>
      ) : authors.length === 0 ? (
        <div className="border border-dashed border-border p-20 text-center">
          <h3 className="font-serif text-3xl font-black">No authors found</h3>
          <p className="mt-4 text-muted-foreground">Try searching for something else or browse the explore feed.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6">
          {authors.map((author, i) => (
            <motion.div
              key={author.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
            >
              <Link to={`/${author.username}`} className="group block border border-border bg-card p-6 lift-card h-full flex flex-col">
                <div className="flex items-center gap-4 mb-6">
                  {author.profile_picture ? (
                    <img src={fileUrl(author.profile_picture)} alt="" className="h-16 w-16 object-cover transition-all duration-500" />
                  ) : (
                    <div className="h-16 w-16 bg-secondary flex items-center justify-center font-serif text-3xl">
                      {(author.name || author.username)[0]}
                    </div>
                  )}
                  <div className="overflow-hidden">
                    <h3 className="font-serif text-xl font-bold truncate group-hover:underline underline-offset-4">{author.name || author.username}</h3>
                    <div className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest mt-0.5">/{author.username}</div>
                  </div>
                </div>

                <div className="flex-grow">
                  <h4 className="font-serif text-lg font-medium leading-tight mb-2 line-clamp-2 italic opacity-80 group-hover:opacity-100 transition-opacity">
                    {author.site_title || "My Journal"}
                  </h4>
                  <p className="text-xs text-muted-foreground line-clamp-3 leading-relaxed mb-4">
                    {author.site_tagline || author.bio || "No description yet."}
                  </p>
                </div>

                <div className="mt-auto pt-6 border-t border-border/50 flex items-center justify-between">
                  <div className="flex items-center gap-4 text-[10px] font-black uppercase tracking-[0.2em]">
                    <span>{author.articles_count} {author.articles_count === 1 ? "Essay" : "Essays"}</span>
                    <span className="text-muted-foreground/50">|</span>
                    <span className="text-muted-foreground">{author.total_views || 0} Views</span>
                  </div>
                  <ArrowRight size={14} className="text-muted-foreground group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      )}

      {meta?.has_more && (
        <div className="mt-20 text-center border-t border-border pt-12">
          <button
            onClick={loadMore}
            disabled={loading}
            className="font-serif text-xl font-black border-b-4 border-foreground pb-2 hover:text-muted-foreground hover:border-muted-foreground transition-all"
          >
            {loading ? "Finding more..." : "Show more authors"}
          </button>
        </div>
      )}
    </div>
  );
}
